import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Button, Input, Message, Segment } from "semantic-ui-react";
import toastr from "toastr";
import { createGovFeePaymentLinkApi } from "../../../../../../../../../apis/applicantApi";
import {
  GOV_FEE_LINK_BLOCK,
  buildGovFeeCustomerMessage,
  describeGovFeePaymentLinkError,
  formatGovFeeAmount,
  formatGovFeeLinkExpiry,
  getGovFeeLinkBrandName,
} from "../govFeePaymentLink";

const BLOCKED_BUTTON_TEXT = {
  [GOV_FEE_LINK_BLOCK.GOV_FEE_ALREADY_PAID]: "Government fee already paid",
  [GOV_FEE_LINK_BLOCK.ROUTE_UNAVAILABLE]: "Payment link unavailable",
};

const copyText = async (text, successMessage) => {
  try {
    await navigator.clipboard.writeText(text);
    toastr.success(successMessage, "Copied!", { timeOut: 1500 });
  } catch (error) {
    toastr.error("Copy failed. Select the text and copy it by hand.");
  }
};

const Breakdown = ({ link }) => {
  const { breakdown = {}, currency } = link;
  const rows = [
    ["Government fee", formatGovFeeAmount(breakdown.govFee, currency)],
    ["Subtotal", formatGovFeeAmount(breakdown.subtotal, currency)],
    [
      `Card processing fee (${breakdown.processingFeeRate ?? 0}%)`,
      formatGovFeeAmount(breakdown.processingFeeAmount, currency),
    ],
    ["Total", formatGovFeeAmount(breakdown.total ?? link.amount, currency)],
  ];
  return (
    <dl className="gov-fee-payment-link__breakdown">
      {rows.map(([label, value]) => (
        <React.Fragment key={label}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </React.Fragment>
      ))}
    </dl>
  );
};

// "Get payment link" action of the DS-82 Charge Fee modal. The customer pays the
// government fee on the returned link; the cart records it as a fee on the
// original order and etaapi reconciliation picks it up.
const GovFeePaymentLink = ({
  applicantId,
  api,
  selectedProduct,
  disabled,
  govFeeAlreadyCharged,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [link, setLink] = useState(null);
  const [error, setError] = useState(null);
  const [block, setBlock] = useState(null);
  const requestRef = useRef(0);
  const selectedProductId = selectedProduct?.value ?? null;

  // A link belongs to one applicant and product; drop it when either changes.
  useEffect(() => {
    requestRef.current += 1;
    setIsLoading(false);
    setLink(null);
    setError(null);
  }, [applicantId, api, selectedProductId]);

  useEffect(() => {
    setBlock(null);
  }, [applicantId, api]);

  const createLink = useCallback(async () => {
    if (!selectedProduct) {
      toastr.error("Please select a fee option");
      return;
    }
    const requestId = ++requestRef.current;
    setIsLoading(true);
    setError(null);
    setLink(null);
    try {
      const response = await createGovFeePaymentLinkApi(applicantId, api, {
        id: selectedProduct.value,
        price: selectedProduct.price || 0,
      });
      if (requestId !== requestRef.current) {
        return;
      }
      const nextLink = response.data?.data;
      if (!nextLink?.url) {
        setError("The server did not return a payment link. Try again.");
        return;
      }
      setLink(nextLink);
    } catch (requestError) {
      if (requestId !== requestRef.current) {
        return;
      }
      const described = describeGovFeePaymentLinkError(requestError);
      setError(described.message);
      setBlock(described.block);
    } finally {
      if (requestId === requestRef.current) {
        setIsLoading(false);
      }
    }
  }, [applicantId, api, selectedProduct]);

  if (block === GOV_FEE_LINK_BLOCK.ENTRY_ORDER) {
    return <Message info content={error} />;
  }

  const buttonBlock = govFeeAlreadyCharged
    ? GOV_FEE_LINK_BLOCK.GOV_FEE_ALREADY_PAID
    : block;
  const brandName = link ? getGovFeeLinkBrandName(link) : "";
  const expiresAtText = link ? formatGovFeeLinkExpiry(link.expiresAt) : "";

  return (
    <div className="gov-fee-payment-link">
      <Button
        secondary
        type="button"
        icon="linkify"
        content={BLOCKED_BUTTON_TEXT[buttonBlock] || "Get payment link"}
        loading={isLoading}
        disabled={Boolean(buttonBlock) || disabled || isLoading || !selectedProduct}
        onClick={createLink}
      />
      {error && <Message negative content={error} />}
      {link && (
        <Segment className="gov-fee-payment-link__result">
          <p>
            Payment page brand: <strong>{brandName}</strong>
          </p>
          <p>
            Total:{" "}
            <strong>
              {formatGovFeeAmount(
                link.breakdown?.total ?? link.amount,
                link.currency,
              )}
            </strong>
            {expiresAtText && <span> · Expires {expiresAtText}</span>}
          </p>
          <Input
            fluid
            readOnly
            value={link.url}
            aria-label="Government fee payment link"
            action={{
              type: "button",
              icon: "copy",
              content: "Copy link",
              onClick: () => copyText(link.url, "Payment link copied"),
            }}
          />
          <Breakdown link={link} />
          <Message
            info
            size="small"
            content="An internal note with this link was added to the Zendesk ticket. The customer was not contacted."
          />
          <Button
            type="button"
            icon="copy"
            content="Copy customer message"
            onClick={() =>
              copyText(
                buildGovFeeCustomerMessage(link),
                "Customer message copied",
              )
            }
          />
        </Segment>
      )}
    </div>
  );
};

GovFeePaymentLink.propTypes = {
  applicantId: PropTypes.string.isRequired,
  api: PropTypes.string.isRequired,
  selectedProduct: PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    price: PropTypes.number,
  }),
  disabled: PropTypes.bool,
  govFeeAlreadyCharged: PropTypes.bool,
};

GovFeePaymentLink.defaultProps = {
  selectedProduct: null,
  disabled: false,
  govFeeAlreadyCharged: false,
};

export default GovFeePaymentLink;
